import { IconButton, useTheme } from "@mui/material";
import {
	CircleArrowDownIcon,
	EllipsisIcon,
	FileTextIcon,
	ImageIcon,
} from "lucide-react";
import { type FC, type MouseEvent, useState } from "react";
import { useIsDocumentTransferPending } from "web_myplann/Api/Document/useIsDocumentTransferPending";
import { useOpenDocumentMutation } from "web_myplann/Api/Document/useOpenDocumentMutation";
import { useSavedDocumentIdsQuery } from "web_myplann/Api/Document/useSavedDocumentIdsQuery";
import type { DocumentDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentActionsDrawerRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentActionsDrawerRedesign";
import { StyledNotDownloadedIcon } from "web_myplann/Portfolio/Components/Redesign/Documents/StyledNotDownloadedIcon";
import { documentSubtitle } from "web_myplann/Portfolio/Helpers/documentSubtitle";
import { RedesignListItemRow } from "web_myplann/Shared/Components/Redesign/RedesignListItemRow";
import { isImageMimeType } from "web_myplann/Shared/Utils/isImageMimeType";

type Props = {
	item: DocumentDTO;
	activeTag?: string;
	showFileSize?: boolean;
};

export const DocumentItemRedesign: FC<Props> = ({
	item,
	activeTag,
	showFileSize = false,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const { mutate: openDocument } = useOpenDocumentMutation();
	const isProcessing = useIsDocumentTransferPending();
	const { data: savedDocumentIds } = useSavedDocumentIdsQuery();
	const showNotDownloaded = Boolean(
		savedDocumentIds && !savedDocumentIds.includes(item.id),
	);
	const DocumentIcon = isImageMimeType(item.mimeType)
		? ImageIcon
		: FileTextIcon;

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const handleOpenDocument = () => {
		if (isProcessing) {
			return;
		}
		openDocument({ id: item.id, fallbackTitle: item.title ?? undefined });
	};

	const handleOpenDrawer = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		if (isProcessing) {
			return;
		}
		setIsDrawerOpen(true);
	};

	return (
		<>
			<RedesignListItemRow
				icon={
					<DocumentIcon
						size={theme.iconSize.md}
						color={theme.palette.grey[500]}
					/>
				}
				title={item.title}
				subtitle={
					<>
						{showNotDownloaded && (
							<StyledNotDownloadedIcon>
								<CircleArrowDownIcon
									size={theme.iconSize.xs}
									color={theme.palette.blue[600]}
								/>
							</StyledNotDownloadedIcon>
						)}
						{documentSubtitle(item, showFileSize)}
					</>
				}
				onClick={handleOpenDocument}
				rightSlot={
					<IconButton
						aria-label={t(Resources.Portfolio.List.DocumentActionsDrawer.title)}
						onClick={handleOpenDrawer}
					>
						<EllipsisIcon
							size={theme.iconSize.sm}
							color={theme.palette.grey[400]}
						/>
					</IconButton>
				}
			/>

			<DocumentActionsDrawerRedesign
				item={item}
				open={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				activeTag={activeTag}
			/>
		</>
	);
};
